import type { QueryClient } from '@tanstack/react-query';
import { refreshBenchmark, throttleRefresh } from './refresh-benchmark';

/**
 * The most often a run of uploads re-reads the benchmark. Each file asks twice (its record exists, its file landed),
 * so 30 files would otherwise make 60 refreshes of three lists each; this keeps it to one read every few seconds
 * however many files there are, and the first record still shows right away.
 */
const UPLOAD_REFRESH_INTERVAL_MS = 3_000;

interface BenchmarkUploadState {
  uploadingImageIds: ReadonlySet<string>;
  failedImageIds: ReadonlySet<string>;
  uploadLandedAt: string | null;
  isRefreshing: boolean;
}

interface BenchmarkUploadStore {
  getSnapshot: () => BenchmarkUploadState;
  subscribe: (listener: () => void) => () => void;
  markUpload: (imageId: string, state: 'uploading' | 'done' | 'failed') => void;
  /** Records a landing by the API's clock; a record without one leaves the previous landing in place. */
  markLanded: (createdAt: string | null | undefined) => void;
  /** Asks for a refresh of the benchmark's lists through its throttle, and counts them as unread until it has run. */
  requestRefresh: () => void;
}

const EMPTY_BENCHMARK_UPLOAD_STATE: BenchmarkUploadState = {
  uploadingImageIds: new Set(),
  failedImageIds: new Set(),
  uploadLandedAt: null,
  isRefreshing: false,
};

/** The later of two timestamps: uploads land in any order, and the latest record is the one to wait on. */
function latestOf(current: string | null, next: string): string {
  return current && Date.parse(current) > Date.parse(next) ? current : next;
}

function withMember(ids: ReadonlySet<string>, id: string, isMember: boolean): ReadonlySet<string> {
  if (ids.has(id) === isMember) {
    return ids;
  }
  const next = new Set(ids);
  if (isMember) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
}

function createBenchmarkUploadStore(
  queryClient: QueryClient,
  workspaceId: string,
  benchmarkId: string,
): BenchmarkUploadStore {
  let state = EMPTY_BENCHMARK_UPLOAD_STATE;
  const listeners = new Set<() => void>();

  const update = (changes: Partial<BenchmarkUploadState>) => {
    const next = { ...state, ...changes };
    if ((Object.keys(next) as (keyof BenchmarkUploadState)[]).every((key) => Object.is(next[key], state[key]))) {
      return;
    }
    state = next;
    for (const listener of listeners) {
      listener();
    }
  };

  const refresh = throttleRefresh(
    () => refreshBenchmark(queryClient, workspaceId, benchmarkId),
    UPLOAD_REFRESH_INTERVAL_MS,
    { onIdle: () => update({ isRefreshing: false }) },
  );

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    markUpload: (imageId, upload) =>
      update({
        uploadingImageIds: withMember(state.uploadingImageIds, imageId, upload === 'uploading'),
        failedImageIds: withMember(state.failedImageIds, imageId, upload === 'failed'),
      }),
    markLanded: (createdAt) => {
      if (createdAt) {
        update({ uploadLandedAt: latestOf(state.uploadLandedAt, createdAt) });
      }
    },
    requestRefresh: () => {
      update({ isRefreshing: true });
      refresh();
    },
  };
}

// Uploads run in the app-wide upload queue and outlive the page, so what they have done to a benchmark is kept here
// rather than in the page: a page opened again mid-upload picks up the running batch and its refresh throttle.
// Keyed by query client, as the refreshes are, so separate clients (as in tests) never share uploads.
const stores = new WeakMap<QueryClient, Map<string, BenchmarkUploadStore>>();

/** The upload state of one benchmark, shared by every page that shows it for as long as the app runs. */
function getBenchmarkUploadStore(queryClient: QueryClient, workspaceId: string, benchmarkId: string) {
  let byBenchmark = stores.get(queryClient);
  if (!byBenchmark) {
    byBenchmark = new Map();
    stores.set(queryClient, byBenchmark);
  }
  const key = `${workspaceId}/${benchmarkId}`;
  let store = byBenchmark.get(key);
  if (!store) {
    store = createBenchmarkUploadStore(queryClient, workspaceId, benchmarkId);
    byBenchmark.set(key, store);
  }
  return store;
}

export { EMPTY_BENCHMARK_UPLOAD_STATE, getBenchmarkUploadStore };
export type { BenchmarkUploadState, BenchmarkUploadStore };
